/**
 * Emoji removal for text the merchant types.
 *
 * Invoices are documents: they are rendered to PDF, emailed, and shown to the
 * merchant's own customers. Emoji survive none of that reliably -- they drop out
 * of PDF fonts, mangle in plain-text mail, and widen a line in ways the invoice
 * layout does not account for. Keeping them out of the field is simpler than
 * handling them everywhere downstream.
 *
 * ── What counts as an emoji here ────────────────────────────────────────────
 *
 * `Extended_Pictographic` is the Unicode property for characters that render as
 * pictures. It deliberately includes ones with no emoji presentation by default
 * (❤, ☺), because a merchant reads those as emoji whether or not the font shows
 * them in colour.
 *
 * The rest of the pattern catches what makes emoji *sequences*: skin-tone
 * modifiers, regional indicators (flags are two of them), the zero-width joiner
 * that builds 👨‍👩‍👧‍👦, the keycap combiner, and variation selector-16.
 *
 * ── What is deliberately kept ───────────────────────────────────────────────
 *
 * © ® ™ ℗ ℠ ‼ ⁉ are Extended_Pictographic too, but they are ordinary typography
 * -- a terms-and-conditions block routinely contains "© 2026 Acme Ltd." and a
 * product name routinely carries ™. Stripping those would corrupt real invoice
 * text, so they are exempt. A trailing U+FE0F is still removed from them, so
 * "©️" becomes the text form "©".
 *
 * Currency signs, arrows, maths symbols, em dashes and non-Latin scripts are
 * untouched: none of them are pictographs.
 */

const EMOJI_PATTERN =
  /\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|[️‍⃣]/gu;

const KEEP = new Set(['©', '®', '™', '℗', '℠', '‼', '⁉']);

/**
 * Remove emoji from a string, leaving everything else as it was.
 * @param {string} value
 * @returns {string}
 */
export const stripEmoji = (value) =>
  String(value ?? '').replace(EMOJI_PATTERN, (ch) => (KEEP.has(ch) ? ch : ''));

/**
 * True when the value contains at least one emoji. For validation messages,
 * where saying what is wrong beats silently rewriting the field.
 * @param {string} value
 * @returns {boolean}
 */
export const hasEmoji = (value) => stripEmoji(value) !== String(value ?? '');

/**
 * True when the value contains an HTML tag or comment (`<b>`, `</div>`,
 * `<!-- -->`). "Size <10" or "x < y > z" is not a tag. Mirrors the backend's
 * hasHtmlTag so the form and the API agree on what is refused.
 * @param {string} value
 * @returns {boolean}
 */
export const hasHtmlTag = (value) => /<\/?[a-z!][^>]*>/i.test(String(value ?? ''));

export const HTML_TAG_MESSAGE = 'cannot contain HTML tags';

export default stripEmoji;
